import { TextCutter } from '@anytag/shared-atoms';
import { useSelectItem } from '@anytag/shared-hooks';
import { InfluencerAvatar } from '../../../../influencer-avatar';
import { MediaPreviewSlider, Tabs, TabsProps } from '../components';
import { DetailsTemplate, DetailsTemplateProps } from './details-template';

type ComparePostsDetailsPostType = DetailsTemplateProps['data'];

export interface CompareTemplateProps<Id extends number = number> {
  data: Record<Id, ComparePostsDetailsPostType[]>;
  accounts: { id: Id; name?: string | null; avatar?: string | null }[];
}

export const CompareTemplate = <Id extends number>({ data, accounts }: CompareTemplateProps<Id>) => {
  const { selectedItem, setSelectedItem } = useSelectItem<{ selectedAccountId: Id; slideIndex: number }>();
  const tabs = accounts.reduce<TabsProps<Id>['tabs']>(
    (acc, curr) =>
      data[curr.id]?.length
        ? [
            ...acc,
            {
              label: (
                <div className="flex min-w-max items-center gap-1">
                  <InfluencerAvatar size="34px" src={curr.avatar} className="rounded-full" />
                  <TextCutter text={curr.name} className="text-base" />
                </div>
              ),
              value: curr.id
            }
          ]
        : acc,
    []
  );

  return (
    <Tabs tabs={tabs} onChange={(selectedAccountId) => setSelectedItem({ selectedAccountId, slideIndex: 0 })}>
      {({ selectedTab: selectedAccountId }) => {
        const postData = selectedAccountId ? data[selectedAccountId] || [] : [];
        const { images, mappedPostIndexByImageIndex } = postData.reduce<{
          images: string[];
          mappedPostIndexByImageIndex: number[];
        }>(
          (acc, curr, index) => {
            acc.images = [...acc.images, ...(curr?.images || [])];
            if (curr?.images?.length) {
              acc.mappedPostIndexByImageIndex = [
                ...acc.mappedPostIndexByImageIndex,
                ...Array(curr.images.length).fill(index)
              ];
            }

            return acc;
          },
          { images: [], mappedPostIndexByImageIndex: [] }
        );
        const currentMediaIndex = selectedItem?.slideIndex || 0;
        const currentPostIndex =
          (selectedItem?.selectedAccountId === selectedAccountId &&
            selectedItem?.slideIndex &&
            mappedPostIndexByImageIndex[selectedItem.slideIndex]) ||
          0;

        return (
          <DetailsTemplate
            data={{ ...postData[currentPostIndex], images } as ComparePostsDetailsPostType}
            mediaPreviewProps={{
              currentMediaIndex,
              onMediaChange: (slideIndex) => setSelectedItem({ slideIndex, selectedAccountId })
            }}
          >
            {images.length > 1 ? (
              <MediaPreviewSlider
                data={images}
                currentMediaIndex={currentMediaIndex}
                onMediaChange={(slideIndex) => setSelectedItem({ slideIndex, selectedAccountId })}
              />
            ) : null}
          </DetailsTemplate>
        );
      }}
    </Tabs>
  );
};
